import React, { useEffect, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, Screen, SearchBar } from '@components'

import { Scope } from '@utils'
import ContactsFilter from '../components/ContactsFilter'
import { ContactsList } from '../../contacts/listingContacts/components'
import WebMap from './components/WebMap'
import { buildMapContactsFilter } from './mapContactsFilter'
import { resolveMyMapPosition } from './myMapPosition'
import { useMyMapVisibility } from './useMyMapVisibility'
import { useMapContacts } from './useMapContacts'
import { useGetProfile, useScreen } from '@hooks'
import { Coords, getCurrentPosition } from '@utils'

const DEFAULT_REGION = {
  lat: 46.603354, // Centre de la France
  lng: 1.888334,
};

// Street level around me; a wide view (western Europe) while my position is
// unknown, rather than a street-level piece of countryside.
const MY_POSITION_ZOOM = 13;
const NO_POSITION_ZOOM = 4;

// Fixed width of the right-hand contacts panel, so the zoom slider can sit
// just to its left when it's open (and at the map's edge when collapsed).
const PANEL_WIDTH = 340;

export const MapScreen: React.FC = () => {
  const theme = useTheme()
  const styles = getQRCodeStyles(theme)
  const navigation = useNavigation()
  const { isMobile } = useScreen()

  const mapRef = useRef<any>(null)

  // "Me" on the map: the browser's live position, else the last one saved on my profile.
  const { data: myProfile } = useGetProfile()
  const [livePosition, setLivePosition] = useState<Coords | null>(null)
  const myPosition = resolveMyMapPosition(livePosition, myProfile)

  // Open the map on me when my saved position is already known, else on the middle of France.
  const [mapCenter, setMapCenter] = useState<[number, number]>(() =>
    myPosition
      ? [myPosition.latitude, myPosition.longitude]
      : [DEFAULT_REGION.lat, DEFAULT_REGION.lng],
  )
  const hasCenteredOnMe = useRef(myPosition != null)

  const [selectedContactId, setSelectedContactId] = useState<string>();
  const [scope, setScope] = useState<Scope[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  // Phone-width browser: the panel would cover the whole map, so it starts
  // closed and lies over the map once opened.
  const [isCollapsed, setIsCollapsed] = useState(isMobile)
  useEffect(() => {
    if (isMobile) setIsCollapsed(true)
  }, [isMobile])

  // The chips are plain filters: none -> contacts and ghost profiles together
  // (ghosts placed approximately by their city); the ghost chip -> ghost
  // profiles only; Privé / Pro narrow both.
  const { flatData: allContacts, isLoading: isLoadingContacts, hasNextPage: hasNextPageContacts, fetchNextPage: fetchNextPageContacts, isFetchingNextPage: isFetchingNextPageContacts } = useMapContacts({
    ...buildMapContactsFilter(scope),
    search: searchText,
  })
  const nearByContacts = allContacts
    .filter((contact) => contact.latitude != null && contact.longitude != null)
    .map((contact) => ({ ...contact, lat: contact.latitude!, lng: contact.longitude! }))

  // Eye button: my visibility on my contacts' maps (and whether my live
  // position is shared with them).
  const visibility = useMyMapVisibility(myProfile?.accountId, livePosition)

  useEffect(() => {
    getCurrentPosition().then((coords) => {
      if (coords) {
        setLivePosition(coords)
        setMapCenter([coords.latitude, coords.longitude])
      }
    })
  }, [])

  // My saved position arrived after the map opened, and no live position (yet):
  // go there once. The live position still wins when it arrives.
  const savedLatitude = myProfile?.latitude
  const savedLongitude = myProfile?.longitude
  useEffect(() => {
    if (hasCenteredOnMe.current || livePosition || savedLatitude == null || savedLongitude == null)
      return
    hasCenteredOnMe.current = true
    setMapCenter([savedLatitude, savedLongitude])
  }, [livePosition, savedLatitude, savedLongitude])

  // What the map needs to draw me; nothing while my position is unknown.
  const myMarker = myPosition
    ? { lat: myPosition.latitude, lng: myPosition.longitude, avatarUrl: myProfile?.avatarUrl }
    : null

  const onContactClick = (contactId: string) => {
    setSelectedContactId(contactId);
    (navigation as any).navigate('Profile', { screen: 'DetailsGhostProfile', params: { profileId: contactId } });
  }

  const toggleModal = () => {
    setIsCollapsed(!isCollapsed)
  }

  return (
    <Screen noPaddingBottom noPaddingTop>
      <WebMap
        ref={mapRef}
        center={mapCenter}
        zoom={myPosition ? MY_POSITION_ZOOM : NO_POSITION_ZOOM}
        style={styles.map}
        contacts={nearByContacts}
        onContactClick={onContactClick}
        rightOffset={isCollapsed || isMobile ? 16 : PANEL_WIDTH + 16}
        me={myMarker}
        isHidden={visibility.isHidden}
        onToggleVisibility={visibility.isReady ? visibility.toggle : undefined}
        isVisibilityPending={visibility.isPending}
      />
      <View style={[styles.modal, !isCollapsed && { width: PANEL_WIDTH, maxWidth: '85%' }]}>
        <View style={styles.closeButton} testID="map-panel-toggle">
          <Icon name={isCollapsed? "arrowLeft" : "arrowRight"} size={16} color={theme.colors.lightGrey} onPress={toggleModal} />
        </View>
        {!isCollapsed && <View>
          <View style={styles.padding}>
            <View style={styles.searchBarContainer}><SearchBar value={searchText} onChangeText={setSearchText} /></View>
            <ContactsFilter
              scope={scope}
              onScopeChange={setScope}
            />
          </View>
          <ContactsList noRightSection contacts={nearByContacts} selectedContactId={selectedContactId}
            onContactPress={(contact) => {
              setSelectedContactId(contact.id);
              mapRef?.current?.zoomToContact(contact);
              // On a phone the open panel hides the map: close it to show them.
              if (isMobile) setIsCollapsed(true)
            }}
            isLoading={isLoadingContacts}
            hasNextPage={hasNextPageContacts}
            fetchNextPage={fetchNextPageContacts}
            isFetchingNextPage={isFetchingNextPageContacts}
          />
        </View>}
      </View>
    </Screen>
  )
}

const getQRCodeStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: theme.spacing.xl,
      paddingHorizontal: theme.spacing.lg,
    },
    content: {
      flex: 1,
    },
    map: {
      flex: 1
    },
    marker: {
      width: 40,
      height: 40,
      borderRadius: 20,
      borderWidth: 2,
      borderColor: 'white',
      overflow: 'hidden'
    },
    modal: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      backgroundColor: theme.colors.white,
      zIndex: 1000
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing.xs,
      left: -(15 + theme.spacing.lg),
      padding: theme.spacing.sm,
      paddingRight: theme.spacing.lg,
      borderTopLeftRadius: theme.border.extraLarge,
      borderBottomLeftRadius: theme.border.extraLarge,
      backgroundColor: theme.colors.white,
    },
    padding: {
      padding: theme.spacing.md,
    },
    searchBarContainer: {
      marginBottom: theme.spacing.md,
    },
    listContent: {
      height: 300,
      paddingTop: theme.spacing.lg,
      paddingBottom: theme.spacing.xxxl
    }
  })
